import styles from './nlm-grid.module.scss';
import { ValueFormatterParams } from 'ag-grid-community';
import { useContext, useState } from 'react';
import { useGetRequestNewHook, useMakeRequestHook } from '../../requestUtils';
import { GetCurrentCompany } from '../../customer-context/customer-context';
import { booleanHeader, Checkbox, CreateHeaders, DatePicker, Dialog, DialogActions, DialogContent, DialogTitle, GrayOutlinedButton, HeaderSizeEnum, prettyPrintXML, PrimaryButton, SnackBarContext, textHeader } from '@frontend-workspace/react-shared-lib';
import WebkuaTable from '../../webkua-table/webkua-table';
import { useTranslation } from 'react-i18next';

/* eslint-disable-next-line */
export interface NlmGridProps {
}

export function NlmGrid(props: NlmGridProps) {
  const { t } = useTranslation();
  const makeRequest = useMakeRequestHook();
  const currentCompany = GetCurrentCompany();
  const { data } = useGetRequestNewHook(`companies/${currentCompany?.number}/licenses/forGroup/true/onlyActive/true`, [], [currentCompany]);
  const { setSnackbarMessage } = useContext(SnackBarContext);
  const companyNumber = currentCompany ? currentCompany.number : "";
  const [expirationDate, setExpirationDate] = useState(new Date().setFullYear(new Date().getFullYear() + 1));
  const [selectedLicenses, setSelectedLicenses] = useState<Array<number>>([]);
  const [downloadLink, setDownloadLink] = useState<string>("");
  const [XMLstring, setXMLstring] = useState<string>("");

  const handleCreateFile = () => {
    const dateObject = new Date(expirationDate);
    const requestBody = {
      licenseNumbers: selectedLicenses,
      expirationDate: dateObject,
      companyNumber: companyNumber,
      xmlString: XMLstring
    }
    setXMLstring("");
    makeRequest(`companies/${companyNumber}/action/createXmlOrder`, 'POST', requestBody).then(result => {
      setSnackbarMessage({ content: t("nlmGrid.NlmFileWillBeCreatedAndWillBeSentToYouByEmail"), type: "success" });
    });
  };

  const SelectUserRenderer = (params: ValueFormatterParams) => {
    const onChange = () => selectedLicenses.includes(params.value) ?
      setSelectedLicenses(selectedLicenses.filter(l => l !== params.value)) :
      setSelectedLicenses(selectedLicenses.concat(params.value));
    return (
      <Checkbox
        checked={selectedLicenses.includes(params.value)}
        onChange={onChange}
      />
    );
  };

  const generateNLM = () => {
    const dateObject = new Date(expirationDate);
    if (expirationDate) {
      const requestBody = {
        licenseNumbers: selectedLicenses,
        expirationDate: dateObject,
        companyNumber: companyNumber
      }
      makeRequest(`companies/${companyNumber}/action/getOfflineXML`, 'POST', requestBody).then(result => {
        setXMLstring(result.xml);
      });
    }
  };
  const productNameRenderer = (params: ValueFormatterParams) => {
    const moduleName = params.data.moduleNames;
    return <span title={moduleName}> {params.value}</span>;
  }
  const headers = CreateHeaders([
    booleanHeader("licenseNumber", "", { renderer: SelectUserRenderer }),
    textHeader("licenseNumber", t("standard.licenseNumber"), HeaderSizeEnum.EXTRA_SMALL),
    textHeader("productName", t("standard.productName"), HeaderSizeEnum.MEDIUM, { renderer: productNameRenderer }),
    textHeader("numberOfSeats", t("standard.numberOfSeats"), HeaderSizeEnum.SMALL, {
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    textHeader("contractNumber", t("standard.contract"), HeaderSizeEnum.SMALL),
    booleanHeader("isOffline", t("standard.offline"))
  ]);

  return <>
    <div className="d-flex flex-row align-items-end flex-wrap">
      <div className='mb-3 mb-xxl-0 me-4'>
        <DatePicker
          inputFormat="dd.MM.yyyy"
          label={t("standard.expiryDate")}
          value={expirationDate}
          onChange={(newDate: any) => {
            setExpirationDate(newDate);
          }}
        />
      </div>
    </div>
    <WebkuaTable
      headers={headers}
      data={data}
    />
    <div className="d-flex flex-row align-items-end mt-3">
      <div className='me-3'>
        {!downloadLink ?
          <PrimaryButton
            variant="contained"
            onClick={generateNLM}>
            {t("nlmGrid.generateNlmFile")}
          </PrimaryButton> :
          <PrimaryButton
            variant="contained"
            download="CnwLicense.dat"
            href={downloadLink}>
            {t("nlmGrid.downloadFile")}
          </PrimaryButton>
        }
      </div>

      {!!XMLstring && <XmlDialog
        XMLstring={XMLstring}
        onClose={() => setXMLstring("")}
        onCreateFile={handleCreateFile}
      />}

    </div>
  </>;
}
function XmlDialog(props: {
  XMLstring: string,
  onClose: () => void,
  onCreateFile: () => void
}) {
  const { t } = useTranslation();
  const { XMLstring } = props;

  return <Dialog open={!!XMLstring} maxWidth="xxl">
    <DialogTitle>
      {t("nlmGrid.offlineNlmFile")}
    </DialogTitle>
    <DialogContent>
      <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {prettyPrintXML(XMLstring)}
      </pre>
    </DialogContent>
    <DialogActions>
      <PrimaryButton
        variant="contained"
        size='large'
        onClick={props.onCreateFile}
        className='minButtonWidth'>
        {t("nlmGrid.createFile")}
      </PrimaryButton>
      <GrayOutlinedButton
        variant="outlined"
        size='large'
        onClick={props.onClose}
        className='minButtonWidth'>
        {t("standard.cancel")}
      </GrayOutlinedButton>
    </DialogActions>
  </Dialog>
}

export default NlmGrid;